import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Button, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Paper,
  Container, CircularProgress, TextField, FormControl, InputLabel, Select, MenuItem,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { collection, getDocs, query, where,} from "firebase/firestore";
import { db } from "../../../firebase";
import Autocomplete from "@mui/material/Autocomplete";
import * as XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: theme.shadows[3],
  marginBottom: theme.spacing(4),
}));

const SalesDetails = ({ unitCode, companyCode, unitType, companyName, }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const printRef = useRef();

  const queryParams = new URLSearchParams(location.search);

  const urlUnitCode =
    queryParams.get("unit") || location.state?.unitCode || "";

  const effectiveUnitCode = urlUnitCode || unitCode;

  const [salesData, setSalesData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const today = new Date().toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [serviceArea, setServiceArea] = useState("All");
  const [serviceAreas, setServiceAreas] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("All");
  const [items, setItems] = useState([]);
  const [itemName, setItemName] = useState("All");

  useEffect(() => {
  const fetchServiceAreas = async () => {
    try {
      const serviceAreaQuery = query(
        collection(db, "serviceAreas"),
        where("companyCode", "==", companyCode),
        where("unitCode", "==", effectiveUnitCode)
      );

      const snapshot = await getDocs(serviceAreaQuery);

      const areas = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setServiceAreas(areas);
    } catch (err) {
      console.error("Error fetching service areas:", err);
    }
  };

  if (effectiveUnitCode) {
    fetchServiceAreas();
  }
}, [effectiveUnitCode, companyCode]);

useEffect(() => {
  const fetchCategories = async () => {
    try {
      const categoryQuery = query(
        collection(db, "categories"),
        where("companyCode", "==", companyCode),
        where("unitCode", "==", effectiveUnitCode)
      );

      const snapshot = await getDocs(categoryQuery);

      const categoryList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setCategories(categoryList);
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };

  if (effectiveUnitCode) {
    fetchCategories();
  }
}, [effectiveUnitCode, companyCode]);


useEffect(() => {
  const fetchItems = async () => {
    try {
      const itemsQuery = query(
        collection(db, "companymenu"),
        where("companyCode", "==", companyCode),
        where("unitCode", "==", effectiveUnitCode)
      );

      const snapshot = await getDocs(itemsQuery);

      const itemList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setItems(itemList);
    } catch (err) {
      console.error("Error fetching items:", err);
    }
  };

  if (effectiveUnitCode) {
    fetchItems();
  }
}, [effectiveUnitCode, companyCode]);

  useEffect(() => {
    const fetchSalesDetails = async () => {
      if (!effectiveUnitCode) {
        setError("Unit code is missing.");
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);

        const ordersQuery = query(
          collection(db, "orders"),
          where("unitCode", "==", effectiveUnitCode)
        );

        const snapshot = await getDocs(ordersQuery);

        let details = [];

        snapshot.docs.forEach((doc) => {
          const order = {
            id: doc.id,
            ...doc.data(),
          };

          if (
            order.billNo === undefined ||
            order.billNo === null ||
            order.billNo === "" ||
            Number(order.billNo) <= 0
          ) {
            return;
          }

          if (!order.createdAt?.toDate) {
            return;
          }

          const billDateObj = order.createdAt.toDate();

          const billDate = billDateObj
            .toISOString()
            .split("T")[0];

          const matchesDate =
            billDate >= startDate &&
            billDate <= endDate;

         const matchesServiceArea =
  serviceArea === "All" ||
  (order.items || []).some(
    (item) => item.serviceArea === serviceArea
  );

if (!matchesDate || !matchesServiceArea) {
  return;
}

          const items = order.items || [];

items
  .filter((item) => {
    const matchesServiceArea =
      serviceArea === "All" ||
      item.serviceArea === serviceArea;

    const matchesCategory =
      category === "All" ||
      item.category === category ||
      item.categoryName === category;

    const matchesItem =
      itemName === "All" ||
      item.itemName === itemName ||
      item.name === itemName;

          // Exclude cancelled items
    const isNotCancelled =
      (item.status || "").toLowerCase() !==
      "cancelled";

    return (
      matchesServiceArea &&
      matchesCategory &&
      matchesItem &&
      isNotCancelled
    );
  })
  .forEach((item) => {
    details.push({
      billNo: order.billNo,
      billDate:
        billDateObj.toLocaleDateString("en-GB"),

      itemCode:
        item.itemCode ||
        item.code ||
        item.id ||
        "N/A",

      itemName:
        item.itemName ||
        item.name ||
        "N/A",

      price: Number(
        item.price || item.rate || 0
      ),

      qty: Number(
        item.quantity || item.qty || 0
      ),

      amount:
        Number(item.price || item.rate || 0) *
        Number(item.quantity || item.qty || 0),
    });
  });
});
        details.sort(
          (a, b) => Number(a.billNo) - Number(b.billNo)
        );

        setSalesData(details);

        setIsLoading(false);
      } catch (err) {
        console.error(err);
        setError("Failed to load sales details.");
        setIsLoading(false);
      }
    };

    fetchSalesDetails();
  }, [
    effectiveUnitCode,
    startDate,
    endDate,
    serviceArea,
    category,
    itemName,
  ]);

const handleExportExcel = () => {
  const workbook = XLSX.utils.book_new();

  const excelData = [];

  // TITLE
  excelData.push([
    `Sales Details - ${companyName || ""}`,
  ]);

  // EMPTY ROW
  excelData.push([]);

  // TABLE HEADERS
  excelData.push([
    "Sl No",
    "Bill No",
    "Bill Date",
    "Item Code",
    "Item Name",
    "Price",
    "Qty",
    "Amount",
  ]);

  const groupedData = {};

  // GROUP DATA
  salesData.forEach((item) => {
    if (!groupedData[item.billDate]) {
      groupedData[item.billDate] = {};
    }

    if (!groupedData[item.billDate][item.billNo]) {
      groupedData[item.billDate][item.billNo] =
        [];
    }

    groupedData[item.billDate][
      item.billNo
    ].push(item);
  });

  let slNo = 1;
  let overallQty = 0;
  let overallAmount = 0;

  Object.entries(groupedData).forEach(
    ([billDate, bills]) => {
      let dateQty = 0;
      let dateAmount = 0;

      // DATE HEADER
      excelData.push([
        `Bill Date : ${billDate}`,
      ]);

      Object.entries(bills).forEach(
        ([billNo, items]) => {
          let billQty = 0;
          let billAmount = 0;

          items.forEach((item, idx) => {
            const qty = Number(
              item.qty || 0
            );

            const amount = Number(
              item.amount || 0
            );

            billQty += qty;
            billAmount += amount;

            dateQty += qty;
            dateAmount += amount;

            overallQty += qty;
            overallAmount += amount;

            excelData.push([
              slNo++,
              idx === 0
                ? `Bill No : ${billNo}`
                : "",
              idx === 0
                ? billDate
                : "",
              item.itemCode,
              item.itemName,
              `₹ ${Number(
                item.price || 0
              ).toFixed(2)}`,
              item.qty,
              `₹ ${Number(
                item.amount || 0
              ).toFixed(2)}`,
            ]);
          });

          // BILL TOTAL
          excelData.push([
            "",
            "",
            "",
            "",
            "Bill Total",
            "",
            billQty,
            `₹ ${billAmount.toFixed(2)}`,
          ]);
        }
      );

      // DATE GRAND TOTAL
      if (
        Object.keys(bills).length > 1
      ) {
        excelData.push([
          "",
          "",
          "",
          "",
          "Grand Total",
          "",
          dateQty,
          `₹ ${dateAmount.toFixed(2)}`,
        ]);
      }

      // EMPTY ROW
      excelData.push([]);
    }
  );

  // OVERALL GRAND TOTAL
  excelData.push([
    "",
    "",
    "",
    "",
    hasMultipleBillsOnAnyDate
      ? "Overall Grand Total"
      : "Grand Total",
    "",
    overallQty,
    `₹ ${overallAmount.toFixed(2)}`,
  ]);

  const worksheet =
    XLSX.utils.aoa_to_sheet(excelData);

  // MERGE CELLS
  worksheet["!merges"] = [];

  // TITLE MERGE
  worksheet["!merges"].push({
    s: { r: 0, c: 0 },
    e: { r: 0, c: 7 },
  });

  // MERGE BILL DATE ROWS
  let rowIndex = 3;

  Object.entries(groupedData).forEach(
    ([billDate, bills]) => {
      worksheet["!merges"].push({
        s: { r: rowIndex, c: 0 },
        e: { r: rowIndex, c: 7 },
      });

      rowIndex++;

      Object.entries(bills).forEach(
        ([billNo, items]) => {
          rowIndex += items.length;
          rowIndex += 1;
        }
      );

      if (
        Object.keys(bills).length > 1
      ) {
        rowIndex += 1;
      }

      rowIndex += 1;
    }
  );

  // COLUMN WIDTHS
  worksheet["!cols"] = [
    { wch: 10 },
    { wch: 22 },
    { wch: 18 },
    { wch: 20 },
    { wch: 35 },
    { wch: 15 },
    { wch: 10 },
    { wch: 18 },
  ];

  // ROW HEIGHTS
worksheet["!rows"] = excelData.map(() => ({
  hpt: 28, // row height
}));

// STYLING + ALIGNMENT
const range =
  XLSX.utils.decode_range(
    worksheet["!ref"]
  );

for (
  let R = range.s.r;
  R <= range.e.r;
  ++R
) {
  for (
    let C = range.s.c;
    C <= range.e.c;
    ++C
  ) {
    const cellAddress =
      XLSX.utils.encode_cell({
        r: R,
        c: C,
      });

    if (!worksheet[cellAddress]) continue;

    // DEFAULT ALIGNMENT
    let horizontalAlign = "left";

    // NUMBER COLUMNS
    if (
      C === 0 || // Sl No
      C === 5 || // Price
      C === 6 || // Qty
      C === 7    // Amount
    ) {
      horizontalAlign = "right";
    }

    // TITLE ROW
    if (R === 0) {
      horizontalAlign = "center";
    }

    // HEADER ROW
    if (R === 2) {
      horizontalAlign = "center";
    }

    // BILL DATE MERGED ROWS
    const rowData = excelData[R];

    if (
      rowData &&
      rowData[0] &&
      String(rowData[0]).startsWith(
        "Bill Date :"
      )
    ) {
      horizontalAlign = "center";
    }

    worksheet[cellAddress].s = {
      alignment: {
        horizontal: horizontalAlign,
        vertical: "center",
      },

      font: {
        bold: R === 0, // ONLY TITLE BOLD
      },
    };
  }
}

  XLSX.utils.book_append_sheet(
    workbook,
    worksheet,
    "Sales Details"
  );

  // FORMAT DATE
  const currentDate = new Date();

  const formattedDate =
    currentDate
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      .replace(/ /g, "_");

  const excelBuffer = XLSX.write(
    workbook,
    {
      bookType: "xlsx",
      type: "array",
      cellStyles: true,
    }
  );

  const data = new Blob(
    [excelBuffer],
    {
      type:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    }
  );

  saveAs(
    data,
    `Sales_Details_${formattedDate}.xlsx`
  );
};

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");

    printWindow.document.write(`
      <html>
        <head>
          <title>Sales Details</title>

          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
            }

            h2 {
              text-align: center;
              margin-bottom: 20px;
            }

            table {
              width: 100%;
              border-collapse: collapse;
            }

            table, th, td {
              border: 1px solid #000;
            }

            th, td {
              padding: 8px;
              text-align: center;
            }
          </style>
        </head>

        <body>
          <h2>
            Sales Details - ${companyName || ""}
          </h2>

          <table>
  <tr>
    <th>Sl No</th>
    <th>Bill No</th>
    <th>Bill Date</th>
    <th>Item Code</th>
    <th>Item Name</th>
    <th>Price</th>
    <th>Qty</th>
    <th>Amount</th>
  </tr>

  ${(() => {
    const groupedData = {};

    salesData.forEach((item) => {
      if (!groupedData[item.billDate]) {
        groupedData[item.billDate] = {};
      }

      if (!groupedData[item.billDate][item.billNo]) {
        groupedData[item.billDate][item.billNo] = [];
      }

      groupedData[item.billDate][item.billNo].push(item);
    });

    let slNo = 1;
    let overallQty = 0;
    let overallAmount = 0;

    return Object.entries(groupedData)
      .map(([billDate, bills]) => {
        let dateQty = 0;
        let dateAmount = 0;

        return `
          <tr style="background:#e0e0e0; font-weight:bold;">
            <td colspan="8">
              Bill Date : ${billDate}
            </td>
          </tr>

          ${Object.entries(bills)
            .map(([billNo, items]) => {
              let billQty = 0;
              let billAmount = 0;

              const rows = items
                .map((item, idx) => {
                  const qty = Number(item.qty || 0);
                  const amount = Number(item.amount || 0);

                  billQty += qty;
                  billAmount += amount;

                  dateQty += qty;
                  dateAmount += amount;

                  overallQty += qty;
                  overallAmount += amount;

                  return `
                    <tr>
                      <td>${slNo++}</td>

                      <td>
                        ${
                          idx === 0
                            ? `Bill No : ${billNo}`
                            : ""
                        }
                      </td>

                      <td>
                        ${
                          idx === 0
                            ? billDate
                            : ""
                        }
                      </td>

                      <td>${item.itemCode}</td>

                      <td>${item.itemName}</td>

                      <td>
                        ₹ ${item.price.toFixed(2)}
                      </td>

                      <td>${item.qty}</td>

                      <td>
                        ₹ ${item.amount.toFixed(2)}
                      </td>
                    </tr>
                  `;
                })
                .join("");

              return `
                ${rows}

                <tr style="background:#f5f5f5; font-weight:bold;">
                  <td colspan="6" align="right">
                    Bill Total
                  </td>

                  <td>${billQty}</td>

                  <td>
                    ₹ ${billAmount.toFixed(2)}
                  </td>
                </tr>
              `;
            })
            .join("")}

          ${
            Object.keys(bills).length > 1
              ? `
              <tr style="background:#dfe6e9; font-weight:bold;">
                <td colspan="6" align="right">
                  Grand Total
                </td>

                <td>${dateQty}</td>

                <td>
                  ₹ ${dateAmount.toFixed(2)}
                </td>
              </tr>
            `
              : ""
          }
        `;
      })
      .join("") +
      `
        <tr style="background:#cfd8dc; font-weight:bold;">
          <td colspan="6" align="right">
            ${
              hasMultipleBillsOnAnyDate
                ? "Overall Grand Total"
                : "Grand Total"
            }
          </td>

          <td>${overallQty}</td>

          <td>
            ₹ ${overallAmount.toFixed(2)}
          </td>
        </tr>
      `;
  })()}
</table>

          <script>
            window.onload = () => {
              window.print();
              window.onafterprint = () => window.close();
            };
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
  };

  const handleBack = () => {
    navigate("/unit_manager/home", {
      state: {
        unitCode: effectiveUnitCode,
        companyCode,
        companyName,
        unitType,
      },
    });
  };

  useEffect(() => {
  setItemName("All");
}, [category]);

const totalQty = salesData.reduce(
  (sum, item) => sum + Number(item.qty || 0),
  0
);

const totalAmount = salesData.reduce(
  (sum, item) => sum + Number(item.amount || 0),
  0
);

const groupedData = {};

salesData.forEach((item) => {
  if (!groupedData[item.billDate]) {
    groupedData[item.billDate] = {};
  }

  if (!groupedData[item.billDate][item.billNo]) {
    groupedData[item.billDate][item.billNo] = [];
  }

  groupedData[item.billDate][item.billNo].push(item);
});

const hasMultipleBillsOnAnyDate =
  Object.values(groupedData).some(
    (bills) => Object.keys(bills).length > 1
  );

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: "center" }}>
        <CircularProgress />

        <Typography sx={{ mt: 2 }}>
          Loading sales details...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: "center" }}>
        <Typography color="error">
          {error}
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box ref={printRef}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            fontWeight: "bold",
            color: "primary.main",
            mb: 4,
            textAlign: "center",
          }}
        >
          Sales Details - {companyName}
        </Typography>

        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: 3,
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) =>
              setStartDate(e.target.value)
            }
            InputLabelProps={{
              shrink: true,
            }}
          />

          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) =>
              setEndDate(e.target.value)
            }
            InputLabelProps={{
              shrink: true,
            }}
          />

          <FormControl sx={{ minWidth: 220 }}>
  <InputLabel>Service Area</InputLabel>

  <Select
    value={serviceArea}
    label="Service Area"
    onChange={(e) =>
      setServiceArea(e.target.value)
    }
  >
    <MenuItem value="All">
      All
    </MenuItem>

    {serviceAreas.map((area) => (
      <MenuItem
        key={area.id}
        value={area.id}  
      >
        {area.serviceAreaName}  {/* display name */}
      </MenuItem>
    ))}
  </Select>
</FormControl>
<Autocomplete
  sx={{ minWidth: 250 }}
  options={[
    { label: "All", value: "All" },
    ...categories.map((cat) => ({
      label: cat.name,
      value: cat.name,
    })),
  ]}
  getOptionLabel={(option) =>
    typeof option === "string"
      ? option
      : option.label || ""
  }
  isOptionEqualToValue={(option, value) =>
    option.value === value.value
  }
  value={
    [
      { label: "All", value: "All" },
      ...categories.map((cat) => ({
        label: cat.name,
        value: cat.name,
      })),
    ].find((c) => c.value === category) || {
      label: "All",
      value: "All",
    }
  }
  onChange={(event, newValue) => {
    setCategory(
      newValue?.value || "All"
    );
  }}
  renderInput={(params) => (
    <TextField
      {...params}
      label="Category"
    />
  )}
/>

<Autocomplete
  sx={{ minWidth: 250 }}
  options={[
    { label: "All", value: "All" },

    ...items
      .filter((itm) => {
        if (category === "All") return true;

        return (
          itm.category === category ||
          itm.categoryName === category
        );
      })
      .map((itm) => ({
        label: itm.name || itm.itemName,
        value: itm.name || itm.itemName,
      })),
  ]}
  getOptionLabel={(option) =>
    typeof option === "string"
      ? option
      : option.label || ""
  }
  isOptionEqualToValue={(option, value) =>
    option.value === value.value
  }
  value={
    [
      { label: "All", value: "All" },

      ...items
        .filter((itm) => {
          if (category === "All") return true;

          return (
            itm.category === category ||
            itm.categoryName === category
          );
        })
        .map((itm) => ({
          label: itm.name || itm.itemName,
          value: itm.name || itm.itemName,
        })),
    ].find((i) => i.value === itemName) || {
      label: "All",
      value: "All",
    }
  }
  onChange={(event, newValue) => {
    setItemName(
      newValue?.value || "All"
    );
  }}
  renderInput={(params) => (
    <TextField
      {...params}
      label="Item Name"
    />
  )}
/>
        </Box>

        <StyledCard>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "primary.light" }}>
                  <TableCell sx={{ fontWeight: "bold", color: "#fff" }}> Sl No </TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "#fff" }}> Bill No </TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "#fff" }}> Bill Date </TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "#fff" }}> Item Code </TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "#fff" }}> Item Name </TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "#fff" }}> Price </TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "#fff" }}> Qty </TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "#fff" }}> Amount </TableCell>
                </TableRow>
              </TableHead>

              {/*<TableBody>
                {salesData.length > 0 ? (
                  salesData.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{item.billNo}</TableCell>
                      <TableCell>{item.billDate}</TableCell>
                      <TableCell>{item.itemCode}</TableCell>
                      <TableCell>{item.itemName}</TableCell>
                      <TableCell>₹ {item.price.toFixed(2)}</TableCell>
                      <TableCell>{item.qty}</TableCell>
                      <TableCell>₹ {item.amount.toFixed(2)}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      align="center"
                    >
                      No sales details found
                    </TableCell>
                  </TableRow>
                )}
              <TableRow
  sx={{
    bgcolor: "#f5f5f5",
  }}
>
  <TableCell
    colSpan={6}
    align="right"
    sx={{
      fontWeight: "bold",
      fontSize: 16,
    }}
  >
    Total
  </TableCell>

  <TableCell
    sx={{
      fontWeight: "bold",
      fontSize: 16,
    }}
  >
    {totalQty}
  </TableCell>

  <TableCell
    sx={{
      fontWeight: "bold",
      fontSize: 16,
    }}
  >
    ₹ {totalAmount.toFixed(2)}
  </TableCell>
</TableRow>
              </TableBody>*/}

         <TableBody>
  {salesData.length > 0 ? (
    <>
      {(() => {
        const groupedData = {};

        // GROUP BY DATE -> BILL NO
        salesData.forEach((item) => {
          if (!groupedData[item.billDate]) {
            groupedData[item.billDate] = {};
          }

          if (
            !groupedData[item.billDate][item.billNo]
          ) {
            groupedData[item.billDate][item.billNo] =
              [];
          }

          groupedData[item.billDate][
            item.billNo
          ].push(item);
        });

        let overallQty = 0;
        let overallAmount = 0;
        let slNo = 1;

        return Object.entries(groupedData).map(
          ([billDate, bills]) => {
            let dateQty = 0;
            let dateAmount = 0;

            return (
              <React.Fragment key={billDate}>
                {/* DATE HEADER */}
                <TableRow
                  sx={{
                    bgcolor: "#e0e0e0",
                  }}
                >
                  <TableCell
                    colSpan={8}
                    sx={{
                      fontWeight: "bold",
                    }}
                  >
                    Bill Date : {billDate}
                  </TableCell>
                </TableRow>

                {Object.entries(bills).map(
                  ([billNo, items]) => {
                    let billQty = 0;
                    let billAmount = 0;

                    return (
                      <React.Fragment
                        key={billNo}
                      >
                        {/* ITEMS */}
                        {items.map(
                          (item, idx) => {
                            const qty =
                              Number(
                                item.qty || 0
                              );

                            const amount =
                              Number(
                                item.amount || 0
                              );

                            billQty += qty;
                            billAmount += amount;

                            dateQty += qty;
                            dateAmount += amount;

                            overallQty += qty;
                            overallAmount +=
                              amount;

                            return (
                              <TableRow
                                key={`${billNo}-${idx}`}
                              >
                                <TableCell>
                                  {slNo++}
                                </TableCell>

                                <TableCell>
                                  {idx === 0
                                    ? `Bill No : ${billNo}`
                                    : ""}
                                </TableCell>

                                <TableCell>
                                  {idx === 0
                                    ? billDate
                                    : ""}
                                </TableCell>

                                <TableCell>
                                  {
                                    item.itemCode
                                  }
                                </TableCell>

                                <TableCell>
                                  {
                                    item.itemName
                                  }
                                </TableCell>

                                <TableCell>
                                  ₹{" "}
                                  {item.price.toFixed(
                                    2
                                  )}
                                </TableCell>

                                <TableCell>
                                  {item.qty}
                                </TableCell>

                                <TableCell>
                                  ₹{" "}
                                  {item.amount.toFixed(
                                    2
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          }
                        )}

                        {/* BILL TOTAL */}
                        <TableRow
                          sx={{
                            bgcolor:
                              "#f5f5f5",
                          }}
                        >
                          <TableCell
                            colSpan={6}
                            align="right"
                            sx={{
                              fontWeight:
                                "bold",
                            }}
                          >
                            Bill Total
                          </TableCell>

                          <TableCell
                            sx={{
                              fontWeight:
                                "bold",
                            }}
                          >
                            {billQty}
                          </TableCell>

                          <TableCell
                            sx={{
                              fontWeight:
                                "bold",
                            }}
                          >
                            ₹{" "}
                            {billAmount.toFixed(
                              2
                            )}
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  }
                )}

                {/* DATE GRAND TOTAL */}
                {/* DATE GRAND TOTAL */}
{Object.keys(bills).length > 1 && (
  <TableRow
    sx={{
      bgcolor: "#dfe6e9",
    }}
  >
    <TableCell
      colSpan={6}
      align="right"
      sx={{
        fontWeight: "bold",
        color: "primary.main",
      }}
    >
      Grand Total
    </TableCell>

    <TableCell
      sx={{
        fontWeight: "bold",
      }}
    >
      {dateQty}
    </TableCell>

    <TableCell
      sx={{
        fontWeight: "bold",
      }}
    >
      ₹ {dateAmount.toFixed(2)}
    </TableCell>
  </TableRow>
)}
              </React.Fragment>
            );
          }
        );
      })()}

      {/* OVERALL GRAND TOTAL */}
      {/* OVERALL TOTAL */}
<TableRow
  sx={{
    bgcolor: "#cfd8dc",
  }}
>
  <TableCell
    colSpan={6}
    align="right"
    sx={{
      fontWeight: "bold",
      fontSize: 16,
    }}
  >
    {hasMultipleBillsOnAnyDate
      ? "Overall Grand Total"
      : "Grand Total"}
  </TableCell>

  <TableCell
    sx={{
      fontWeight: "bold",
      fontSize: 16,
    }}
  >
    {salesData.reduce(
      (sum, item) =>
        sum + Number(item.qty || 0),
      0
    )}
  </TableCell>

  <TableCell
    sx={{
      fontWeight: "bold",
      fontSize: 16,
    }}
  >
    ₹{" "}
    {salesData
      .reduce(
        (sum, item) =>
          sum + Number(item.amount || 0),
        0
      )
      .toFixed(2)}
  </TableCell>
</TableRow>
    </>
  ) : (
    <TableRow>
      <TableCell
        colSpan={8}
        align="center"
      >
        No sales details found
      </TableCell>
    </TableRow>
  )}
</TableBody>
            </Table>
          </TableContainer>
        </StyledCard>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: 2,
          mt: 3,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={handlePrint}
        >
          Print
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleExportExcel}
        >
          Excel
        </Button>
        <Button
          variant="outlined"
          onClick={handleBack}
        >
          Back
        </Button>
      </Box>
    </Container>
  );
};

export default SalesDetails;